import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { LeaseTractFormFieldName } from '../lib/form-layout/lease-tract-fields';

export type LeaseTractLayoutDocument = LayoutDocument<LeaseTractFormFieldName>;

export interface ResolvedLayout {
  layout: LeaseTractLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const leaseTractFormLayoutKeys = {
  all: ['lease-tract-form-layout'] as const,
  active: (projectId: string | null) =>
    [...leaseTractFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...leaseTractFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useLeaseTractFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: leaseTractFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/lease-tract-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

export function useAdminActiveLeaseTractLayout(scope: 'global' | 'project', projectId: string | null) {
  return useQuery({
    queryKey: leaseTractFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/lease-tract-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateLeaseTractLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: LeaseTractLayoutDocument;
  notes?: string | null;
}

export function useUpdateLeaseTractFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateLeaseTractLayoutInput) =>
      api.put<ResolvedLayout>('/admin/lease-tract-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: leaseTractFormLayoutKeys.all }),
  });
}
